import type { PostHogConfig, PostHogInterface } from "posthog-js";

type PostHogArg = string | number | boolean | null | undefined | object;

type QueuedCall =
    | { kind: "call"; args: PostHogArg[]; name: string }
    | { kind: "ready"; run: () => void };

type PostHogBootClient = {
    init: (apiKey: string, options?: Partial<PostHogConfig>) => void;
};

type PostHogLoader = () => Promise<PostHogBootClient>;

const queuedCalls: QueuedCall[] = [];
let client: PostHogBootClient | null = null;
let bootPromise: Promise<void> | null = null;

function callClient(target: object, name: string, args: PostHogArg[]): void {
    const method = Reflect.get(target, name);
    if (typeof method !== "function") return;
    Reflect.apply(method, target, args);
}

function bindClient(next: PostHogBootClient) {
    client = next;
    const pending = queuedCalls.splice(0, queuedCalls.length);
    for (const call of pending) {
        if (call.kind === "ready") call.run();
        else callClient(next, call.name, call.args);
    }
}

export function runWhenPostHogReady<TClient extends object>(
    target: TClient,
    run: (ready: TClient) => void,
) {
    if (target !== deferredPostHog || client !== null) {
        run(target);
        return;
    }
    if (typeof window === "undefined") return;
    queuedCalls.push({ kind: "ready", run: () => run(target) });
}

async function loadRealPostHog(): Promise<PostHogBootClient> {
    const loaded = await import("posthog-js/dist/module.js");
    return loaded.default;
}

export function resetDeferredPostHogForTests() {
    client = null;
    queuedCalls.length = 0;
    bootPromise = null;
}

export function bootDeferredPostHog(
    apiKey: string,
    options?: Partial<PostHogConfig>,
    load: PostHogLoader = loadRealPostHog,
): Promise<void> {
    if (typeof window === "undefined") return Promise.resolve();
    if (bootPromise) return bootPromise;
    bootPromise = load()
        .then((posthog) => {
            posthog.init(apiKey, options);
            bindClient(posthog);
        })
        .catch((error: Error) => {
            bootPromise = null;
            console.error("[posthog] boot failed", error);
        });
    return bootPromise;
}

export const deferredPostHog = new Proxy(
    {} as PostHogInterface & PostHogBootClient,
    {
        get(_target, property) {
            if (typeof property !== "string") return undefined;
            if (client) {
                const value = Reflect.get(client, property);
                return typeof value === "function" ? value.bind(client) : value;
            }
            return (...args: PostHogArg[]) => {
                if (typeof window === "undefined") return undefined;
                if (client) {
                    callClient(client, property, args);
                    return undefined;
                }
                if (property === "capture" && args[0] === "$pageview") {
                    const options = args[2];
                    args[2] = {
                        timestamp: new Date(),
                        ...(typeof options === "object" && options !== null
                            ? options
                            : {}),
                    };
                }
                queuedCalls.push({ kind: "call", name: property, args });
                return undefined;
            };
        },
    },
);

export default deferredPostHog;
